import React from 'react';
import { useTranslation } from 'react-i18next';
import { truncateText, sortBy, convertRToSuperscript } from '../utils/utils';
import Img from 'gatsby-image/withIEPolyfill';
import { GatsbySanityImageFluid } from '../types';

import * as styles from './ResourceArticlesListSectionPage.module.scss';
import ResourcesPageLayout from '../components/ResourcesPageLayout';
import ReadMoreLink from '../components/ReadMoreLink';
import GenericLink from '../components/GenericLink';
import SearchBox from '../components/SearchBox';
import { useUpdateUrlFromFilters, usePagination } from '../utils/hooks';
import SEO from '../components/SEO';

interface ResourceArticle {
  title: string;
  image?: {
    asset?: {
      fluid: GatsbySanityImageFluid;
    };
  };
  date?: string;
  extract?: string;
  slug: {
    current: string;
  };
}

interface ResourceArticlesListSectionPageProps {
  pageContext: {
    displayMode: 'cards' | 'titlesAndExtracts' | 'titlesOnly';
    title: string;
    slug: {
      current: string;
    };
    intro: string;
    resourceArticles: Array<ResourceArticle>;
  };
}

const ResourceArticlesListSectionPage = ({
  pageContext: resourceArticlesSection,
}: ResourceArticlesListSectionPageProps): React.ReactElement => {
  const { t } = useTranslation();
  const { resourceArticles } = resourceArticlesSection;

  const {
    currentPage,
    currentPageItems: currentPageArticles,
    getPageUrlPart,
    renderPagination,
  } = usePagination(
    resourceArticles,
    resourceArticlesSection.displayMode === 'titlesOnly' ? 20 : 10,
  );

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    return [getPageUrlPart()];
  }, [currentPage]);

  function parseDate(dateStr: string): Date {
    return new Date(dateStr + 'T00:00:00Z');
  }

  function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  return (
    <ResourcesPageLayout
      currentSlug={resourceArticlesSection.slug.current}
      withPageContentBox
      searchBox={
        <SearchBox<never, never>
          contentType="resource_article"
          entriesGroups={[
            {
              entries: sortBy(resourceArticlesSection.resourceArticles, 'title').map(
                resourceArticle => ({
                  name: resourceArticle.title,
                  url:
                    '/resources/' +
                    resourceArticlesSection.slug.current +
                    '/' +
                    resourceArticle.slug.current,
                }),
              ),
            },
          ]}
          noResultsMessage={t(
            'resource_articles_list_page.search_box.no_search_results_text',
            'No resources match the searched keyword.',
          )}
          renderNamePart={convertRToSuperscript}
        ></SearchBox>
      }
    >
      <SEO
        title={resourceArticlesSection.title}
        urlPath={'/resources/' + resourceArticlesSection.slug.current}
      />
      <div className={styles.container}>
        <h2>{resourceArticlesSection.title}</h2>
        {!!resourceArticlesSection.intro && (
          <p className={styles.intro}>{resourceArticlesSection.intro}</p>
        )}
        {resourceArticlesSection.displayMode === 'titlesOnly' &&
          currentPageArticles.map((article, i) => (
            <div key={i} className={styles.linkArticle}>
              <GenericLink
                key={i}
                to={`/resources/${
                  resourceArticlesSection.slug.current + '/' + article.slug.current
                }`}
              >
                {article.title}
              </GenericLink>
            </div>
          ))}
        {resourceArticlesSection.displayMode === 'titlesAndExtracts' &&
          currentPageArticles.map((article, i) => (
            <div key={i} className={styles.article}>
              <h4 className={styles.title}>{convertRToSuperscript(article.title)}</h4>
              {article.extract && (
                <p className={styles.text}>
                  {convertRToSuperscript(truncateText(article.extract, 400))}
                </p>
              )}
              <ReadMoreLink
                to={`/resources/${
                  resourceArticlesSection.slug.current + '/' + article.slug.current
                }`}
              >
                {t('resource_articles_list_page.read_more_link', 'Read More')}
              </ReadMoreLink>
            </div>
          ))}
        {resourceArticlesSection.displayMode === 'cards' && (
          <div className={styles.imageArticlesContainer}>
            {currentPageArticles.map((article, i) => (
              <GenericLink
                noStyle
                to={`/resources/${
                  resourceArticlesSection.slug.current + '/' + article.slug.current
                }`}
                key={i}
                className={styles.card}
              >
                <article>
                  {!!(article.image && article.image.asset) && (
                    <Img
                      className={styles.cardImage}
                      fluid={article.image.asset.fluid}
                      alt={article.title}
                    />
                  )}
                  <div className={styles.cardText}>
                    <span className={styles.title} title={article.title}>
                      {convertRToSuperscript(truncateText(article.title, 60))}
                    </span>
                    {article.date && (
                      <span className={styles.date}>{formatDate(parseDate(article.date))}</span>
                    )}
                    {!!article.extract && (
                      <p className={styles.text}>
                        {convertRToSuperscript(truncateText(article.extract, 200))}
                      </p>
                    )}
                  </div>
                  <ReadMoreLink styleOnly>
                    {t('news_page.read_more_link', 'Read More')}
                  </ReadMoreLink>
                </article>
              </GenericLink>
            ))}
          </div>
        )}
      </div>
      {renderPagination()}
    </ResourcesPageLayout>
  );
};

export default ResourceArticlesListSectionPage;
